import graphql from "babel-plugin-relay/macro";
import classNames from "classnames";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { FinishedLoadingNotification } from "components/shared/FinishedLoadingNotification";
import { accountMessages } from "domain/account/accountMessages";
import { confirmAccount, ConfirmAccountFormValues } from "domain/account/confirmAccount";
import { confirmAccountMutationResponse } from "generatedQueries/confirmAccountMutation.graphql";
import { ConfirmFormQuery } from "generatedQueries/ConfirmFormQuery.graphql";
import { FC } from "react";
import { MessageDescriptor } from "react-intl";
import { useLazyLoadQuery } from "react-relay";
import { Card, CardBody, CardHeader, FormText } from "reactstrap";
import { HideableContainer, HideableFields } from "shared/components/Hideable";
import { Message } from "shared/components/Message";
import { ErrorHandlingMutationForm } from "shared/components/newFormInputs/ErrorHandlingMutationForm";
import { Radio } from "shared/components/newFormInputs/Radio";
import { RadioGroup } from "shared/components/newFormInputs/RadioGroup";
import { mapMessages } from "shared/mapMessages";
import { sharedMessages } from "shared/sharedMessages";
import { required } from "shared/validations/validations";

const messages = mapMessages("components.account.ConfirmForm", {
    canDiscloseToPublicHelpText: "Selecting 'No' will exclude your details from the organisation’s mailing lists and prevent sharing with affiliates. However, your information may still be shared with other residents if your pet is found roaming, to facilitate its safe return.",
    canDiscloseToPublicLabel: "Disclose Details",
});

interface IProps {
    nextButtonMessage?: MessageDescriptor;
    onBack: () => void;
    onNext: () => void;
    pageContentName: MessageDescriptor | string;
}

export const ConfirmForm: FC<IProps> = ({
    onNext,
    onBack,
    nextButtonMessage,
    pageContentName,
}) => {
    const { account, configuration } = useLazyLoadQuery<ConfirmFormQuery>(query, {});

    if (!account) {
        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
    }

    return (
        <div className="row justify-content-center" aria-busy="false">
            <div className="col-xl-5 col-lg-6 col-md-7 col-sm-9">
                <FinishedLoadingNotification contentName={pageContentName} />
                <ErrorHandlingMutationForm<ConfirmAccountFormValues, confirmAccountMutationResponse>
                    mutationFunc={confirmAccount}
                    onPrevious={onBack}
                    onNext={onNext}
                    defaultValues={{
                        canDiscloseToPublic: account.canDiscloseToPublic?.toString() ?? null,
                        isSubscribedToShelterNewsletter: account.isSubscribedToShelterNewsletter?.toString() ?? null,
                    }}
                    saveButtonMessage={nextButtonMessage}
                    alwaysSubmit={true}
                >
                    {({ control, register }) =>
                        <Card className={classNames("mt-3", "mb-2")}>
                            <CardHeader>
                                <Message message={accountMessages.additionalDetailsLabel} />
                            </CardHeader>
                            <CardBody>
                                <HideableContainer
                                    field={HideableFields.canDiscloseToPublic}
                                    configuration={configuration}
                                >
                                    <RadioGroup
                                        name="canDiscloseToPublic"
                                        control={control}
                                        register={register}
                                        options={required<ConfirmAccountFormValues, "canDiscloseToPublic">()}
                                        label={messages.canDiscloseToPublicLabel}
                                        isRequired={true}>
                                            {{
                                                radios: (registerRadio) => <>
                                                    <Radio {...registerRadio()} isInline={true} label={sharedMessages.confirmNoLabel} value="false" />
                                                    <Radio {...registerRadio()} isInline={true} label={sharedMessages.confirmYesLabel} value="true" />
                                                </>,
                                                helpText: <FormText>
                                                    <Message message={messages.canDiscloseToPublicHelpText} />
                                                </FormText>,
                                            }}
                                    </RadioGroup>
                                </HideableContainer>
                                <HideableContainer
                                    field={HideableFields.isSubscribedToShelterNewsletter}
                                    configuration={configuration}
                                >
                                    <RadioGroup
                                        name="isSubscribedToShelterNewsletter"
                                        control={control}
                                        register={register}
                                        options={required<ConfirmAccountFormValues, "isSubscribedToShelterNewsletter">()}
                                        label={accountMessages.subscribeToShelterNewsletter}
                                        isRequired={true}>
                                            {{
                                                radios: (registerRadio) => <>
                                                    <Radio {...registerRadio()} isInline={true} label={sharedMessages.confirmNoLabel} value="false" />
                                                    <Radio {...registerRadio()} isInline={true} label={sharedMessages.confirmYesLabel} value="true" />
                                                </>,
                                            }}
                                    </RadioGroup>
                                </HideableContainer>
                            </CardBody>
                        </Card>
                    }
                </ErrorHandlingMutationForm>
            </div>
        </div>
    );
};

const query = graphql`
    query ConfirmFormQuery {
        account {
            canDiscloseToPublic
            isSubscribedToShelterNewsletter
        }
        configuration {
            ...Hideable_configuration
        }
    }`;
